import { GetStaticPropsContext } from 'next';

import { Box, Card, Flex, Heading, Link, ListItem, OrderedList, Text } from '@chakra-ui/react';

import { BlueWaveHero } from '~/components/blue-wave-hero';
import { ContentContainer } from '~/components/container';
import { Glyph } from '~/components/glyph';
import { useCurrency } from '~/features/internationalization/hooks/use-currency';
import { DEFAULT_LANGUAGE } from '~/features/internationalization/languages';
import { PageHead } from '~/features/page/components/page-head';
import { PageLayout } from '~/features/page/components/page-layout';
import { getPage } from '~/features/page/controllers/get-page';
import { Page } from '~/features/page/models/page';
import { usePricePlans } from '~/features/plans/hooks/use-price-plans';
import { CopyToText } from '~/features/reimbursement/components/copy-to-text';

export default function ReimbursementPage({ page }: { page: Page }) {
  const { currency } = useCurrency();
  const { data } = usePricePlans(currency);

  const monthlyPrice = data?.prices?.monthly?.individual?.displayPrice;

  const employerTemplate = `
Hi [manager name],

I'd like to enroll in a program called [program name] on the professional education platform Udacity. Programs at Udacity consist of a high-quality, meticulously designed curriculum, built in collaboration with companies like Google, Facebook, Amazon, Adobe, and Microsoft.

If you’d like to review what I will learn in [program name], please take a look here: [link to course]

Udacity’s learning subscription is unique because I’ll learn via hands-on projects that allow me to apply my new skills in the workplace immediately. I’ll also have access to a mentor network comprised of industry experts I can get feedback from throughout my learning journey.

This new knowledge will help our team by [list reasons]

A Udacity subscription costs ${monthlyPrice} per month. This gets me access to every program on their platform from schools such as Artificial Intelligence, Data Science, and Programming. I will be able to expand my knowledge in multiple areas beneficial to the team through continuous education.

What are your thoughts?

Thanks,
[Your Name]

P.S. Udacity has business plans that allow for entire teams to upskill at once. It might make sense for us to sign up for a plan like this. You can learn more here: https://www.udacity.com/enterprise/overview
  `;

  const businessTemplate = `
Hi [manager name],

I’ve been researching professional education platforms for our team, and would like to share information with you about the best one I found, Udacity. Programs at Udacity consist of a high-quality, meticulously designed curriculum, built in collaboration with companies like Google, Facebook, Amazon, Adobe, and Microsoft.

Here are links to a few of the programs I think would have the biggest impact for us [link program pages].

Udacity’s learning subscription is unique because the team would learn via hands-on projects that would allow us to apply our new skills in the workplace immediately. We’d also have access to a mentor network comprised of industry experts we could get feedback from throughout our learning journeys.

Providing a subscription to our team will help us by [list reasons].

A Udacity subscription gets the team access to every program on their platform from schools such as Artificial Intelligence, Data Science, and Programming. We will be able to expand our knowledge in multiple areas through continuous education.

You can learn more about how Udacity helps entire teams upskill and why this would be great for our development here: https://www.udacity.com/enterprise/overview

What are your thoughts?

Thanks,
[Your Name]
  `;

  return (
    <>
      <PageHead page={page} />
      <PageLayout withoutVerticalPadding>
        <BlueWaveHero>
          <Heading as='h1' size='marketing-4xl' textAlign='center'>
            How to Expense Udacity
          </Heading>
          <Text fontWeight='normal' my='2' size='base' textAlign='center'>
            Get reimbursed for your subscription
          </Text>
        </BlueWaveHero>

        <ContentContainer>
          <Box display='flex' margin='64px auto' maxWidth='730px' textAlign='center'>
            <p>
              Many companies will help your career growth through professional education reimbursement. Learn how to
              talk with your employer to expense a Udacity subscription.
            </p>
          </Box>

          <Flex flexDirection='column' gap='64px' marginBottom='64px'>
            <Card boxShadow='none' gap='60px' padding={{ base: '40px 16px', md: '60px' }}>
              <Box display='flex' flexDirection='column' gap='32px'>
                <Flex
                  alignItems='flex-start'
                  flexDirection={{ base: 'column', md: 'row' }}
                  gap={{ base: '24px', md: '0' }}
                >
                  <Glyph name='paper-curled-one' />
                  <Flex flexDirection='column' gap='24px' padding={{ base: '0', md: '0 32px' }}>
                    <Heading size={{ base: 'marketing-2xl', md: 'marketing-3xl' }}>Download Your Receipt</Heading>
                    <p>
                      After you enroll in a Udacity subscription, you will receive a receipt as proof of payment. You
                      will need to send this receipt to your employer for reimbursement.
                    </p>
                    <OrderedList display='flex' flexDirection='column' gap='16px'>
                      <ListItem>
                        Go to the “Subscription” tab of your account settings by clicking on the icon in the top
                        right-hand corner of your screen when you are logged in.
                      </ListItem>
                      <ListItem>Scroll to the “Billing History” section.</ListItem>
                      <ListItem>
                        (Recommended) To add your name to the receipt, click “Add Details” in the “Receipts will be
                        issued to:” section. This will show your employer that you were the one who purchased a
                        subscription.
                      </ListItem>
                      <ListItem>Click the receipt icon in the billing history table to download the receipt.</ListItem>
                    </OrderedList>
                  </Flex>
                </Flex>
              </Box>

              <Box display='flex' flexDirection='column' gap='32px'>
                <Flex
                  alignItems='flex-start'
                  flexDirection={{ base: 'column', md: 'row' }}
                  gap={{ base: '24px', md: '0' }}
                >
                  <Glyph name='email-email-one' />
                  <Flex flexDirection='column' gap='24px' padding={{ base: '0', md: '0 32px' }}>
                    <Heading size='marketing-3xl'>Email Your Employer</Heading>
                    <p>
                      You will need to speak with your company to figure out the approval process for reimbursement.
                      Some companies have an education benefit you can use that is managed by HR or other departments,
                      while other companies will need specific approval by your manager.
                    </p>
                    <p>
                      Here is an example email template you can use to let your manager know you’d like your education
                      costs with Udacity reimbursed.
                    </p>
                  </Flex>
                </Flex>
                <CopyToText label='Email To Employer' name='employer-template' text={employerTemplate}>
                  <p>
                    Hi{' '}
                    <Text as='span' color='blue.500'>
                      [manager name],
                    </Text>
                  </p>
                  <p>
                    I&apos;d like to enroll in a program called{' '}
                    <Text as='span' color='blue.500'>
                      [program name]
                    </Text>{' '}
                    on the professional education platform Udacity. Programs at Udacity consist of a high-quality,
                    meticulously designed curriculum, built in collaboration with companies like Google, Facebook,
                    Amazon, Adobe, and Microsoft.
                  </p>
                  <p>
                    If you’d like to review what I will learn in{' '}
                    <Text as='span' color='blue.500'>
                      [program name]
                    </Text>
                    , please take a look here:{' '}
                    <Text as='span' color='blue.500'>
                      [link to course]
                    </Text>
                  </p>
                  <p>
                    Udacity’s learning subscription is unique because I’ll learn via hands-on projects that allow me to
                    apply my new skills in the workplace immediately. I’ll also have access to a mentor network
                    comprised of industry experts I can get feedback from throughout my learning journey.
                  </p>
                  <p>
                    This new knowledge will help our team by{' '}
                    <Text as='span' color='blue.500'>
                      [list reasons]
                    </Text>
                  </p>
                  <p>
                    A Udacity subscription costs {monthlyPrice} per month. This gets me access to every program on their
                    platform from schools such as Artificial Intelligence, Data Science, and Programming. I will be able
                    to expand my knowledge in multiple areas beneficial to the team through continuous education.
                  </p>
                  <p>What are your thoughts?</p>
                  <p>
                    Thanks, <br />
                    <Text as='span' color='blue.500'>
                      [Your Name]
                    </Text>
                  </p>
                  <p>
                    P.S. Udacity has business plans that allow for entire teams to upskill at once. It might make sense
                    for us to sign up for a plan like this. You can learn more here:{' '}
                    <Link color='blue.500!' href='https://www.udacity.com/enterprise/overview' isExternal>
                      https://www.udacity.com/enterprise/overview
                    </Link>
                  </p>
                </CopyToText>
              </Box>
            </Card>

            <Card boxShadow='none' gap='60px' padding={{ base: '40px 16px', md: '60px' }}>
              <Box display='flex' flexDirection='column' gap='32px'>
                <Flex
                  alignItems='flex-start'
                  flexDirection={{ base: 'column', md: 'row' }}
                  gap={{ base: '24px', md: '0' }}
                >
                  <Glyph name='office-buildings-one' />
                  <Flex flexDirection='column' gap='24px' padding={{ base: '0', md: '0 32px' }}>
                    <Heading size='marketing-3xl'>Ask Your Employer to sign up for a business plan</Heading>
                    <p>
                      Alternatively, your company can sign up for a business plan, purchasing subscriptions for you and
                      your colleagues directly. Here is an example email template you can use to let your manager know
                      about Udacity business plans for your team’s education.
                    </p>
                  </Flex>
                </Flex>
                <CopyToText label='Email For Business Plan' name='business-template' text={businessTemplate}>
                  <p>
                    Hi{' '}
                    <Text as='span' color='blue.500'>
                      [manager name]
                    </Text>
                    ,
                  </p>
                  <p>
                    I’ve been researching professional education platforms for our team, and would like to share
                    information with you about the best one I found, Udacity. Programs at Udacity consist of a
                    high-quality, meticulously designed curriculum, built in collaboration with companies like Google,
                    Facebook, Amazon, Adobe, and Microsoft.
                  </p>
                  <p>
                    Here are links to a few of the programs I think would have the biggest impact for us{' '}
                    <Text as='span' color='blue.500'>
                      [link program pages].
                    </Text>
                  </p>
                  <p>
                    Udacity’s learning subscription is unique because the team would learn via hands-on projects that
                    would allow us to apply our new skills in the workplace immediately. We’d also have access to a
                    mentor network comprised of industry experts we could get feedback from throughout our learning
                    journeys.
                  </p>
                  <p>
                    Providing a subscription to our team will help us by{' '}
                    <Text as='span' color='blue.500'>
                      [list reasons]
                    </Text>
                    .
                  </p>
                  <p>
                    A Udacity subscription gets the team access to every program on their platform from schools such as
                    Artificial Intelligence, Data Science, and Programming. We will be able to expand our knowledge in
                    multiple areas through continuous education.
                  </p>
                  <p>
                    You can learn more about how Udacity helps entire teams upskill and why this would be great for our
                    development here:{' '}
                    <Link color='blue.500!' href='https://www.udacity.com/enterprise/overview' isExternal>
                      https://www.udacity.com/enterprise/overview
                    </Link>
                  </p>
                  <p>What are your thoughts?</p>
                  <p>
                    Thanks, <br />
                    <Text as='span' color='blue.500'>
                      [Your Name]
                    </Text>
                  </p>
                </CopyToText>
              </Box>
            </Card>

            <Card backgroundColor='accent.sky.100' border='1px' borderColor='gray.300' boxShadow='none' padding='16px'>
              <Flex alignItems='center' gap='5px' justifyContent={{ base: 'center', md: 'left' }}>
                <Glyph height={48} name='chat-bubbles-chat-bubbles-one' width={48} />
                <Flex alignItems='center' flexDirection='row' gap='5px' padding={{ base: '0', md: '0 8px' }}>
                  <Heading fontSize='16px' fontWeight='400'>
                    Need help?
                  </Heading>
                  <Link
                    as='a'
                    colorScheme='blue'
                    fontSize='16px'
                    href='https://support.udacity.com/hc/en-us'
                    isExternal
                  >
                    Contact Us
                  </Link>
                </Flex>
              </Flex>
            </Card>
          </Flex>
        </ContentContainer>
      </PageLayout>
    </>
  );
}

export async function getStaticProps({ locale }: GetStaticPropsContext) {
  // Stop additional locale pages except the default locale from being built
  if (locale !== DEFAULT_LANGUAGE) {
    return {
      notFound: true,
    };
  }

  const page = await getPage('/get-reimbursed');
  return {
    props: {
      page,
    },
  };
}
